import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  ArchiveOutline,
  BriefcaseOutline,
  Close,
  EllipsisVertical,
  MenuOutline,
} from 'react-ionicons';
import clsx from 'clsx';
import AnnounceKit from 'announcekit-react';
import { observer } from 'mobx-react-lite';

import DropdownMenu from './DropdownMenu';
import UserAvatar from 'components/Avatar';
import Notifications from 'components/Notifications/Notifications';
import FeedbackDialog from 'layouts/DefaultLayout/FeedbackDialog/FeedbackDialog';
import { CatalogModal } from 'components/ProductTour/CatalogModal';
import { CompanyAdminsDialog } from 'screens/Home/CompanyAdminsDialog';
import NavbarExpandButton, {
  useNavbarExpandButton,
} from './NavbarExpandButton';
import HomeMenu, { HomeMenuItem } from './HomeMenu';
import {
  AccessExpressIcon,
  EdgebootIcon,
  MobilityIcon,
  NOCExpressIcon,
  TechExpressIcon,
} from './icons';

import getDropdownOptionsByRole from './utils';
import { useFeatureFlags } from 'feature-flags';
import { useNotifications } from 'hooks/useNotifications';
import { useModal } from 'hooks/useModal';

import { NOTIFICATIONS_POLL_INTERVAL } from 'components/Notifications/utils';
import { TokenUserPermissions } from 'api/users/schemas/Users';
import { useAuth } from 'authContext';
import { useAuthUser } from 'hooks/useAuthUser';
import { ScrollableContent } from './ScrollableContent';
import { hasPermission } from 'hooks/usePermissions';
import Divider from 'components/Divider';
import DropdownMenuOptions from './DropdownMenuOptions';
import Logo from 'components/Logo';
import { NavbarLogo } from './NavbarLogo';
import CollapsibleSection from './CollapsibleSection';
import SubGroupDropdown from './SubGroupDropdown';
import { useProductTourContext } from 'context/ProductTourContext';
import SearchButton from './SearchButton';
import ViewsMenu, { ClearHistoryButton } from './ViewsMenu';

const Navbar = observer(() => {
  const { isNavbarExpanded, setIsNavbarExpanded } = useNavbarExpandButton();

  const { flags } = useFeatureFlags();
  const navigate = useNavigate();
  const { logout } = useAuth();
  const { name, companyName, roles, email, group_id } = useAuthUser();
  const [isNotificationsOpen, setNotificationsOpen] = useState(false);
  const handleNotificationsToggle = () => {
    setNotificationsOpen(!isNotificationsOpen);
  };

  const { notificationCount } = useNotifications(
    {
      onlyUnread: true,
      notificationType: 'all',
    },
    {
      refetchInterval: NOTIFICATIONS_POLL_INTERVAL,
      staleTime: NOTIFICATIONS_POLL_INTERVAL,
      enabled: flags.NOTIFICATIONS_ENABLED,
    },
  );

  const switchCompany = () => navigate('/auth/select-company');

  const { open: openFeedbackModal, ...modalProps } = useModal();
  const { open: openCatalogModal, ...catalogModalProps } = useModal();
  const { open: openCompanyAdminsModal, ...companyAdminsProps } = useModal();

  const productTour = useProductTourContext();
  useEffect(() => {
    if (productTour.running) {
      setIsNavbarExpanded(true);
    }
  }, [productTour.running, setIsNavbarExpanded]);
  useEffect(() => {
    if (!productTour.openModalFunction) {
      productTour.setOpenModalFunction(openCatalogModal);
    }
  }, [openCatalogModal, productTour]);

  const userDropdownOptions = getDropdownOptionsByRole({
    name: name || 'John Doe',
    companyName,
    navigate,
    logout,
    switchCompany,
    openFeedbackModal,
    openCatalogModal,
    openCompanyAdminsModal,
    flags,
    email,
  });

  const { pathname, search } = useLocation();

  const homeMenuItems = useMemo(() => {
    const data: HomeMenuItem[] = [
      {
        className: 'product-tour-inventory',
        title: 'Inventory',
        icon: () => (
          <ArchiveOutline color="currentColor" width="24px" height="24px" />
        ),
        to: '/inventory',
        show: hasPermission({
          permission: TokenUserPermissions.INVENTORY_READ,
          flags,
        }),
      },
      {
        className: 'product-tour-my-company',
        title: 'My company',
        icon: BriefcaseOutline,
        to: '/users',
        isActive: pathname === '/',
        show: true,
        subItems: [
          {
            className: 'product-tour-my-company-dashboard',
            title: 'Dashboard',
            to: '/',
            isActive: pathname === '/',
          },
          {
            className: 'product-tour-my-company-user-management',
            title: 'User management',
            to: '/users',
            show: hasPermission({
              permission: TokenUserPermissions.USER_MANAGEMENT,
              flags,
            }),
            isActive: pathname.startsWith('/users'),
          },
          {
            title: 'Company details',
            onClick: () => openCompanyAdminsModal(),
          },
        ],
      },
      {
        title: 'AccessExpress',
        icon: AccessExpressIcon,
        to: '/access-express',
        show: hasPermission({
          permission: TokenUserPermissions.ACCESS_EXPRESS_READ,
          flags,
        }),
        subItems: [
          {
            title: 'Order services',
            to: '/access-express',
            isActive: pathname === '/access-express',
          },
          {
            title: 'My quote requests',
            to: '/access-express/index',
            isActive: pathname === '/access-express/index',
          },
          ...(hasPermission({ onlyForSuperAdmin: true })
            ? [
                {
                  title: 'Access Express 3.0',
                  to: '/access-express/v3',
                  isActive: pathname === '/access-express/v3',
                },
              ]
            : []),
        ],
      },
      {
        className: 'product-tour-tech-express',
        title: 'TechExpress',
        icon: TechExpressIcon,
        to: '/tech-express',
        show: hasPermission({
          permission: TokenUserPermissions.TECH_EXPRESS_READ,
          flags,
        }),
        subItems: [
          {
            className: 'product-tour-tech-express-open-ticket',
            title: 'Open ticket',
            to: '/tech-express/let-us-help',
            isActive: pathname.startsWith('/tech-express/let-us-help'),
          },
          {
            className: 'product-tour-tech-express-my-tickets',
            title: 'My tickets',
            to: '/tech-express',
            isActive: pathname === '/tech-express',
          },
          {
            className: 'product-tour-tech-express-templates',
            title: 'Templates',
            to: '/tech-express/templates',
            isActive: pathname.startsWith('/tech-express/templates'),
            show: hasPermission({
              permission: TokenUserPermissions.TECH_EXPRESS_MANAGE,
              flags,
            }),
          },
        ],
      },
      {
        className: 'product-tour-noc-express',
        title: 'NOCExpress',
        icon: NOCExpressIcon,
        to: '/noc-express',
        show: hasPermission({
          permission: TokenUserPermissions.NOC_EXPRESS_READ,
          flags,
        }),
        subItems: [
          {
            className: 'product-tour-noc-express-open-ticket',
            title: 'Open ticket',
            to: '/noc-express/open-ticket',
            isActive: pathname.startsWith('/noc-express/open-ticket'),
          },
          {
            className: 'product-tour-noc-express-my-tickets',
            title: 'My tickets',
            to: '/noc-express',
            isActive: pathname === '/noc-express' && search === '',
          },
          {
            className: 'product-tour-noc-express-dashboard',
            title: 'Dashboard',
            to: '/noc-express?tab=dashboard',
            isActive:
              pathname.startsWith('/noc-express') &&
              search === '?tab=dashboard',
          },
          // {title: 'Alerting', to: '[TBD]'},
        ],
      },
      {
        title: 'Mobility',
        icon: MobilityIcon,
        to: '/mobility',
        show: hasPermission({
          permission: TokenUserPermissions.MOBILITY_READ,
          flags,
        }),
        subItems: [
          {
            title: 'Mobility Dashboard',
            to: 'https://gemm.granitenet.com/dashboard',
            target: '_blank',
          },
          {
            title: 'Mobility Marketplace',
            to: 'https://marketplace.webmobility.granitenet.net/login',
            target: '_blank',
          },
        ],
      },
      {
        title: 'edgeboot',
        icon: EdgebootIcon,
        to: 'https://edgeboot.io/',
        target: '_blank',
      },
    ];
    return data;
  }, [openCompanyAdminsModal, pathname, search, flags]);

  const collapsibleButtonRef = React.createRef<HTMLDivElement>();

  const [menuDisplay, setMenuDisplay] = useState(false);
  useEffect(() => {
    setMenuDisplay(true);
  }, []);

  const overlayRef = useRef<HTMLDivElement>(null);
  const toggleButtonRef = useRef<HTMLDivElement>(null);
  const [isMobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleMobileMenuToggle = () => {
    setMobileMenuOpen(!isMobileMenuOpen);
  };

  useEffect(() => {
    const closeOnClickOutside = (ev: MouseEvent) => {
      if (
        isMobileMenuOpen &&
        overlayRef.current &&
        ev.target instanceof Node &&
        !overlayRef?.current?.contains(ev.target) &&
        !toggleButtonRef?.current?.contains(ev.target)
      ) {
        setMobileMenuOpen(false);
      }
    };
    if (isMobileMenuOpen) {
      document.addEventListener('mousedown', closeOnClickOutside);
      document.body.style.overflow = 'hidden';
    } else {
      document.removeEventListener('mousedown', closeOnClickOutside);
      document.body.style.overflow = 'auto';
    }

    return () => {
      document.removeEventListener('mousedown', closeOnClickOutside);
      document.body.style.overflow = 'auto';
    };
  }, [isMobileMenuOpen]);

  const handleOptionClick = (groupIndex: number, optionIndex: number) => {
    userDropdownOptions[groupIndex].options[optionIndex].onClick();
    handleMobileMenuToggle();
  };

  return (
    <header
      className={clsx(
        '!duration-500',
        'sticky top-0 z-[50] xl:h-screen',
        'navbar-wrapper',
        'xl:transition-all',
        isNavbarExpanded ? 'xl:w-[230px]' : 'xl:w-[80px]',
      )}
    >
      <div
        className={clsx(
          'h-full w-full',
          'rounded-r bg-[#262636] shadow-elevation4',
          'hidden flex-none flex-col gap-8 px-4 pb-4 pt-8 xl:flex',
          'transition-transform duration-[400ms] ease-in-out',
          menuDisplay
            ? 'translate-x-0'
            : isNavbarExpanded
              ? '-translate-x-[230px]'
              : '-translate-x-[80px]',
        )}
      >
        <NavbarLogo isLarge={isNavbarExpanded} />
        <SubGroupDropdown
          isNavbarExpanded={isNavbarExpanded}
          collapsibleButtonRef={collapsibleButtonRef}
        />
        {flags.GLOBAL_SEARCH && (
          <SearchButton isNavbarExpanded={isNavbarExpanded} />
        )}
        <hr className="h-[1px] border-stroke-base-subdued bg-opacity-50" />
        <NavbarExpandButton
          isExpanded={isNavbarExpanded}
          onClick={() => setIsNavbarExpanded((prev) => !prev)}
          ref={collapsibleButtonRef}
        />
        <ScrollableContent>
          <div
            className={clsx(
              'relative flex flex-col gap-8 duration-500',
              isNavbarExpanded ? 'w-[198px]' : 'w-[48px]',
            )}
          >
            <CollapsibleSection
              label="Menu"
              isWide={isNavbarExpanded}
              isCollapsible={false}
            >
              <HomeMenu
                className="text-xs"
                isWide={isNavbarExpanded}
                items={homeMenuItems}
                collapsibleButtonRef={collapsibleButtonRef}
              />
            </CollapsibleSection>
            {flags.RECENTLY_VIEWED_AND_PINNED_TICKETS && (
              <>
                <hr className="h-[1px] border-stroke-base-subdued bg-opacity-50" />
                <CollapsibleSection
                  label="Views"
                  isWide={isNavbarExpanded}
                  headerSuffix={
                    <ClearHistoryButton isWide={isNavbarExpanded} />
                  }
                >
                  <ViewsMenu isWide={isNavbarExpanded} />
                </CollapsibleSection>
              </>
            )}
          </div>
        </ScrollableContent>

        <div className="flex flex-col gap-6">
          {flags.NOTIFICATIONS_ENABLED && (
            <div
              className="-my-2 w-full cursor-pointer overflow-hidden py-2 hover:text-content-base-default"
              onClick={handleNotificationsToggle}
            >
              <div className="flex w-[198px] flex-row items-center gap-2 text-xs">
                <div
                  className={clsx(
                    'relative fill-content-base-default transition-all duration-300 ease-out',
                    !isNavbarExpanded && 'translate-x-3.5 transform',
                  )}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                  >
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M7.6864 3.14668C7.96594 2.20785 8.78578 1.25 10.0001 1.25C11.2148 1.25 12.0339 2.20789 12.3155 3.14495C12.3169 3.1467 12.3188 3.1489 12.3213 3.15143C12.327 3.15746 12.3336 3.16299 12.3398 3.16729C12.3445 3.17056 12.3473 3.17196 12.3476 3.17213C13.0545 3.46318 13.8866 3.92809 14.5373 4.77928C15.1933 5.63732 15.6201 6.82844 15.6201 8.49023C15.6201 10.1471 15.7949 11.0557 16.0523 11.6794C16.278 12.2263 16.5748 12.5858 16.9971 13.0974C17.0588 13.1721 17.1232 13.25 17.1904 13.3323L17.1906 13.3325C17.9689 14.2859 17.2339 15.625 16.0349 15.625H13.1251C13.1251 16.4538 12.7959 17.2487 12.2099 17.8347C11.6238 18.4208 10.8289 18.75 10.0001 18.75C9.17135 18.75 8.37649 18.4208 7.79044 17.8347C7.20439 17.2487 6.87515 16.4538 6.87515 15.625H3.96929C2.77248 15.625 2.02509 14.2927 2.80991 13.3323C2.87716 13.25 2.94157 13.172 3.0033 13.0972C3.42557 12.5857 3.72241 12.2262 3.94831 11.6793C4.20597 11.0555 4.3812 10.1469 4.38218 8.49023M8.12515 15.625C8.12515 16.1223 8.32269 16.5992 8.67432 16.9508C9.02595 17.3025 9.50287 17.5 10.0001 17.5C10.4974 17.5 10.9743 17.3025 11.326 16.9508C11.6776 16.5992 11.8751 16.1223 11.8751 15.625H8.12515ZM3.96929 14.375C3.84083 14.375 3.783 14.3159 3.75977 14.2704C3.74639 14.2442 3.74239 14.2187 3.74431 14.1974C3.74602 14.1786 3.75298 14.1537 3.77785 14.1232L3.77789 14.1232C3.83825 14.0493 3.89907 13.976 3.95995 13.9026C4.3789 13.3977 4.80116 12.8888 5.10364 12.1565C5.45134 11.3147 5.63118 10.2185 5.63218 8.4906V8.49023C5.63218 7.05058 5.9966 6.14008 6.4573 5.53769C6.92314 4.92857 7.53352 4.57386 8.13057 4.32797L8.13071 4.32792C8.44893 4.19702 8.76643 3.9054 8.8811 3.51467L8.88156 3.51312C9.0594 2.90126 9.51896 2.5 10.0001 2.5C10.4814 2.5 10.9411 2.90097 11.1212 3.51408L11.1217 3.51567C11.2357 3.89988 11.5498 4.19559 11.8716 4.32794C12.4674 4.57322 13.078 4.92862 13.5443 5.53849C14.0054 6.14159 14.3701 7.05242 14.3701 8.49023C14.3701 10.2181 14.5494 11.3144 14.8968 12.1563C15.1991 12.8888 15.6215 13.3978 16.0404 13.9027C16.1013 13.9761 16.1621 14.0493 16.2224 14.1232C16.2615 14.1712 16.2647 14.221 16.2393 14.2711C16.2156 14.3179 16.1587 14.375 16.0349 14.375H3.96929ZM7.6864 3.14668C7.68532 3.14796 7.68397 3.14947 7.68232 3.15118C7.677 3.1567 7.67071 3.16202 7.66444 3.16634C7.65861 3.17035 7.65512 3.17193 7.65512 3.17193L7.65474 3.17208C6.94754 3.46331 6.1152 3.92734 5.46439 4.77833C4.80843 5.63603 4.38224 6.82698 4.38218 8.48987"
                      fill="#F8FAFC"
                    />
                  </svg>
                  {notificationCount > 0 && (
                    <span className="absolute right-[3px] top-[3px] flex h-[6px] w-[6px]">
                      <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-button-background-destructive-default opacity-75"></span>
                      <span className="relative inline-flex h-[6px] w-[6px] rounded-full bg-button-background-destructive-default"></span>
                    </span>
                  )}
                </div>
                <div
                  className={clsx(
                    'flex-1 font-semibold transition-all duration-300 ease-out',
                    !isNavbarExpanded && 'opacity-0',
                  )}
                >
                  Notifications
                </div>
                <div className="min-w-[26px] flex-none rounded border border-stroke-base-heavy p-1 text-center text-content-base-default">
                  {notificationCount}
                </div>
              </div>
            </div>
          )}

          <hr className="h-[1px] border-stroke-base-subdued bg-opacity-50" />

          <div className={clsx(!isNavbarExpanded && 'w-[48px]')}>
            <DropdownMenu
              label={
                <div className="relative flex flex-row gap-2">
                  <div
                    className={clsx(
                      'pointer-events-none absolute flex-none transition-all duration-300 ease-out',
                      !isNavbarExpanded && 'translate-x-2 transform',
                    )}
                  >
                    <UserAvatar
                      width="w-8"
                      height="h-8"
                      name={name || 'John Doe'}
                      fontSize="text-xs"
                    />
                  </div>
                  <div
                    className={clsx(
                      'group w-[196px] overflow-hidden pl-10 transition-all duration-300 ease-out',
                      !isNavbarExpanded && '!w-[0px] opacity-0',
                    )}
                  >
                    <div className="flex w-[156px] flex-1 flex-row items-center gap-2">
                      <div className="flex flex-1 flex-col overflow-hidden text-left text-xs">
                        <div className="truncate font-bold text-content-base-default">
                          {name}
                        </div>
                        <div className="truncate font-normal text-content-base-subdued">
                          {email}
                        </div>
                      </div>
                      <span className="flex-none text-content-base-subdued duration-150 ease-out group-hover:text-content-base-default">
                        <EllipsisVertical
                          color="currentColor"
                          width="16px"
                          height="16px"
                        />
                      </span>
                    </div>
                  </div>
                </div>
              }
              groups={userDropdownOptions}
            />
          </div>
        </div>
        <div className="fixed">
          <AnnounceKit
            widget="https://announcekit.co/widgets/v2/1HUiRi"
            user={{ id: group_id as string, name: name, email: email }}
            data={{
              member_type: roles[0],
            }}
          />
        </div>
      </div>
      {/* Mobile Menu items*/}
      <div className="flex justify-between bg-[#262636] p-2 shadow-elevation4 xl:hidden">
        {/* <div className="flex "> */}
        <Logo className="m-2 !h-8" />
        <div
          ref={toggleButtonRef}
          onClick={handleMobileMenuToggle}
          className={`flex cursor-pointer items-center justify-between gap-3 px-4 py-3 ${
            isMobileMenuOpen ? 'rounded-md bg-background-base-surface-0' : ''
          }`}
        >
          {isMobileMenuOpen ? (
            <div className="rotate-90 transform transition duration-300">
              <Close color={'#F8FAFC'} height="20px" width="20px" />
            </div>
          ) : (
            <div className="transform transition">
              <MenuOutline color={'#F8FAFC'} height="20px" width="20px" />
            </div>
          )}
          <span className="font-bold text-content-base-default">Menu</span>
        </div>
        {/* </div> */}
      </div>
      <div
        className={clsx(
          `fixed inset-0 z-auto mt-[65px] w-full md:hidden`,
          isMobileMenuOpen
            ? 'h-full bg-background-base-overlay opacity-100'
            : 'h-0 opacity-0',
        )}
      />
      <div
        className={`fixed inset-0 mt-[65px] transition-transform duration-200 ease-in-out ${
          isMobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
        } `}
      >
        <div
          ref={overlayRef}
          className={`z-100 ml-auto flex h-full w-[320px] flex-col items-start bg-background-base-surface-0 p-4`}
        >
          <HomeMenu
            isWide={true}
            items={homeMenuItems}
            collapsibleButtonRef={collapsibleButtonRef}
            className="lg:text-sm"
          />
          <div className="my-2 w-full">
            <Divider variant="subdued" />
          </div>
          <DropdownMenuOptions
            options={userDropdownOptions}
            handleOptionClick={handleOptionClick}
            className="flex w-full list-none [&_label]:!text-base lg:[&_label]:!text-sm [&_svg]:!mx-1 [&_svg]:!scale-150 "
          />
        </div>
      </div>
      <Notifications
        isNotificationsOpen={isNotificationsOpen}
        setNotificationsOpen={setNotificationsOpen}
      />
      <FeedbackDialog {...modalProps} />
      <CatalogModal {...catalogModalProps} />
      <CompanyAdminsDialog {...companyAdminsProps} />
    </header>
  );
});

export default Navbar;
